import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  Smile, Frown, Meh, Angry, CloudRain, Sun, Heart,
  Flame, TrendingUp, Users, Activity
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

export default function MoodMap() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [selectedMood, setSelectedMood] = useState('')
  const [intensity, setIntensity] = useState(5)
  const [note, setNote] = useState('')

  const moodOptions = [
    { key: 'happy', label: t('moodMap.mood.happy'), icon: Smile, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { key: 'hopeful', label: t('moodMap.mood.hopeful'), icon: Sun, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { key: 'calm', label: t('moodMap.mood.calm'), icon: Heart, color: 'text-green-500', bg: 'bg-green-500/10' },
    { key: 'grateful', label: t('moodMap.mood.grateful'), icon: Smile, color: 'text-pink-500', bg: 'bg-pink-500/10' },
    { key: 'anxious', label: t('moodMap.mood.anxious'), icon: Meh, color: 'text-blue-400', bg: 'bg-blue-400/10' },
    { key: 'sad', label: t('moodMap.mood.sad'), icon: Frown, color: 'text-blue-600', bg: 'bg-blue-600/10' },
    { key: 'angry', label: t('moodMap.mood.angry'), icon: Angry, color: 'text-red-500', bg: 'bg-red-500/10' },
    { key: 'lonely', label: t('moodMap.mood.lonely'), icon: CloudRain, color: 'text-purple-400', bg: 'bg-purple-400/10' },
  ]

  const { data: moodMap } = useQuery({
    queryKey: ['mood-map'],
    queryFn: () => api.get('/mood/map').then(r => r.data),
    refetchInterval: 30000
  })

  const { data: streak } = useQuery({
    queryKey: ['mood-streak'],
    queryFn: () => api.get('/mood/streak').then(r => r.data),
    enabled: isAuthenticated
  })

  const { data: moodHistory } = useQuery({
    queryKey: ['mood-history'],
    queryFn: () => api.get('/mood/history').then(r => r.data),
    enabled: isAuthenticated
  })

  const submitMutation = useMutation({
    mutationFn: () => api.post('/mood', { mood: selectedMood, intensity, note }),
    onSuccess: () => {
      setSelectedMood('')
      setIntensity(5)
      setNote('')
      queryClient.invalidateQueries({ queryKey: ['mood-map'] })
      queryClient.invalidateQueries({ queryKey: ['mood-history'] })
      queryClient.invalidateQueries({ queryKey: ['mood-streak'] })
      toast({ title: t('moodMap.saved') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const total = moodMap?.total || 1

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Activity className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('moodMap.title')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('moodMap.subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <Users className="w-5 h-5 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold">{total}</p>
          <p className="text-xs text-muted-foreground">{t('moodMap.last24h')}</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <Flame className="w-5 h-5 text-orange-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{streak?.streak || 0}</p>
          <p className="text-xs text-muted-foreground">{t('moodMap.streak')}</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border text-center">
          <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{moodOptions.find(o => o.key === moodMap?.moods?.[0]?._id)?.label || '-'}</p>
          <p className="text-xs text-muted-foreground">{t('moodMap.mostCommon')}</p>
        </div>
      </div>

      {/* Mood Distribution */}
      <div className="mb-8 p-5 rounded-2xl bg-card border border-border">
        <h2 className="font-semibold mb-4">{t('moodMap.distribution')}</h2>
        <div className="space-y-3">
          {moodMap?.moods?.map((m: any) => {
            const moodOpt = moodOptions.find(o => o.key === m._id)
            const Icon = moodOpt?.icon || Smile
            const percentage = Math.round((m.count / total) * 100)
            return (
              <div key={m._id} className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${moodOpt?.color || ''}`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">{moodOpt?.label || m._id}</span>
                    <span className="text-xs text-muted-foreground">{m.count} ({percentage}%)</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      className="h-full rounded-full bg-primary"
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Submit Mood */}
      {isAuthenticated && (
        <div className="mb-8 p-5 rounded-2xl bg-card border border-border">
          <h2 className="font-semibold mb-4">{t('moodMap.howAreYou')}</h2>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {moodOptions.map(mood => (
              <button
                key={mood.key}
                onClick={() => setSelectedMood(mood.key)}
                className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${
                  selectedMood === mood.key
                    ? `${mood.bg} ${mood.color} ring-2 ring-primary/50`
                    : 'hover:bg-secondary'
                }`}
              >
                <mood.icon className={`w-6 h-6 ${mood.color}`} />
                <span className="text-xs">{mood.label}</span>
              </button>
            ))}
          </div>

          {selectedMood && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-4"
            >
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('moodMap.intensity', { intensity })}
                </label>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={intensity}
                  onChange={(e) => setIntensity(Number(e.target.value))}
                  className="w-full accent-primary"
                />
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('moodMap.notePlaceholder')}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[80px] text-sm"
              />
              <Button
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending}
                className="w-full rounded-lg"
              >
                {submitMutation.isPending ? t('moodMap.saving') : t('moodMap.share')}
              </Button>
            </motion.div>
          )}
        </div>
      )}

      {/* Recent Entries */}
      <div className="mb-8 p-5 rounded-2xl bg-card border border-border">
        <h2 className="font-semibold mb-4">{t('moodMap.recentEntries')}</h2>
        <div className="space-y-3">
          {moodMap?.recentEntries?.slice(0, 10)?.map((entry: any) => {
            const moodOpt = moodOptions.find(o => o.key === entry.mood)
            const Icon = moodOpt?.icon || Smile
            return (
              <div key={entry._id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                <Icon className={`w-5 h-5 ${moodOpt?.color || ''}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{moodOpt?.label || entry.mood}</p>
                  {entry.note && <p className="text-xs text-muted-foreground truncate">{entry.note}</p>}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDistanceToNow(entry.createdAt)}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* My Mood History */}
      {isAuthenticated && (
        <div className="p-5 rounded-2xl bg-card border border-border">
          <h2 className="font-semibold mb-4">{t('moodMap.myHistory')}</h2>
          <div className="space-y-3">
            {moodHistory && moodHistory.length > 0 ? (
              moodHistory.map((entry: any, index: number) => {
                const moodOpt = moodOptions.find(o => o.key === entry.mood)
                const Icon = moodOpt?.icon || Smile
                return (
                  <motion.div
                    key={entry._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50"
                  >
                    <Icon className={`w-5 h-5 ${moodOpt?.color || ''}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{moodOpt?.label || entry.mood}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('moodMap.intensity', { intensity: entry.intensity })}
                      </p>
                      {entry.note && (
                        <p className="text-xs text-muted-foreground truncate">{entry.note}</p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatDistanceToNow(entry.createdAt)}
                    </span>
                  </motion.div>
                )
              })
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('moodMap.noHistory')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
